import type React from "react";
import { useMemo } from "react";

import { $enum } from "ts-enum-util";

import { FrostFreeStatus, type PatchBuildingRoomsRequestBody } from "@eisox/api/core-api";
import { Alert, Button, CanAccess, Field } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon } from "@eisox/icons";
import { getDirtyValues, useMediaQuery } from "@eisox/tools";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { roomApi } from "~/api";
import {
  filterPeriods,
  frostFreePeriodsAllowed,
  getDefaultMode,
  getDefaultPeriods,
  getFrostFreeModes,
  getFrostFreeStatusLabel,
  mapPeriodsWithErrors,
  Periods,
} from "~/features/frost-free";
import { holidaysSettingsSchema } from "~/features/room/helpers";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { useRoomContext } from "~/providers/room.provider";

const NAME = "HolidaysSettings";

export const HolidaysSettings: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const queryClient = useQueryClient();

  const { building, gateways } = useBuildingContext(NAME);
  const { room } = useRoomContext(NAME);

  const { mutateAsync: updateRoom } = useMutation({
    mutationFn: (frostFree: PatchBuildingRoomsRequestBody[number]["frostFree"]) =>
      roomApi.update(building.id, [{ ids: [room.id], frostFree }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
    },
  });

  const areFrostFreePeriodsAllowed = frostFreePeriodsAllowed(gateways);

  const defaultValues = useMemo(
    () => ({
      mode: getDefaultMode(room.frostFree.status, getFrostFreeModes({ periodsAllowed: areFrostFreePeriodsAllowed })),
      frostFreePeriods: getDefaultPeriods(room.frostFree.periods),
    }),
    [room.frostFree, areFrostFreePeriodsAllowed],
  );

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: holidaysSettingsSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const { mode, frostFreePeriods } = dirty;
      await updateRoom({
        ...((mode !== undefined || frostFreePeriods !== undefined) && {
          status: value.mode,
          periods: filterPeriods(value.frostFreePeriods),
        }),
      });
      formApi.reset();
    },
  });

  const modeOptions = useMemo(
    () =>
      $enum(FrostFreeStatus)
        .getValues()
        .filter(status => areFrostFreePeriodsAllowed || status !== FrostFreeStatus.PERIODS)
        .map(status => ({ value: status, name: getFrostFreeStatusLabel(status) })),
    [areFrostFreePeriodsAllowed],
  );

  return (
    <form
      className="flex h-full flex-col justify-between"
      data-testid={NAME}
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex h-full flex-col gap-[20px]">
          <form.AppField name="mode">
            {field => (
              <field.Select
                multiple={false}
                options={modeOptions}
                placeholder={m.frost_free_mode()}
                classNames={{ control: "w-full" }}
              />
            )}
          </form.AppField>

          <form.Subscribe selector={state => state.values.mode}>
            {mode =>
              mode === FrostFreeStatus.PERIODS && (
                <form.AppField name="frostFreePeriods" mode="array">
                  {field => (
                    <Field.Root className="flex flex-col gap-[10px]">
                      <Field.Label className="text-primary-blue">{m.holidays_periods()}</Field.Label>
                      <Periods
                        periods={mapPeriodsWithErrors(field.state.value, field.state.meta.errors)}
                        onAddPeriod={period => field.pushValue(period)}
                        onRemovePeriod={index => field.removeValue(index)}
                        onUpdatePeriod={(index, period) => field.replaceValue(index, period)}
                      />
                    </Field.Root>
                  )}
                </form.AppField>
              )
            }
          </form.Subscribe>

          <CanAccess isAuthorized={!isMobile}>
            <Field.Root className="flex flex-col gap-[10px]">
              <Field.Label className="text-primary-blue">{m.building()}</Field.Label>
              <Alert severity="help">{m.room_temperatures_building_help()}</Alert>
              <Button asChild className="flex items-center gap-[10px] text-base text-primary-blue">
                <Link to="/buildings/$buildingId/settings/buildings/holidays" params={{ buildingId: building.id }}>
                  {m.frost_free_temperature()}
                  <ArrowRightIcon />
                </Link>
              </Button>
            </Field.Root>
          </CanAccess>

          <form.SubmitButton className="m-0 mx-auto" label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
};

HolidaysSettings.displayName = NAME;
